import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Provider as ReduxProvider, useSelector } from 'react-redux';

import configureStore from 'store';

import { getCurrency, getDictionary, getLanguage, getMarket } from '@travel/i18n';
import useDeviceType, {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
} from '@travel/traveler-core/hooks/useDeviceType';
import { cx } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';

import { getIsGlobalCurrency } from 'store/markets/selectors';
import { getReviewScoreSummary } from 'store/providerReview/selectors';

import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import type { ProviderInformation } from 'ProviderInformation-Types';
import { getGoogleMapURL } from 'utils/googleMapURL';
import makeMarkerWithLabel from 'utils/markerWithLabel';

import ProviderSummary from '../../components/ProviderSummary';
import MiniProviderSummary from './ProviderSummary';

import styles from './locationMap.module.scss';

type Props = {
  className?: string;
  item: ProviderInformation;
  isOpen: boolean;
  onClose: () => void;
  /** parent page name for RAT popups */
  pageName:
    | 'hotel:rooms_and_plans_dated_list'
    | 'hotel:rooms_and_plans_undated_list'
    | 'reservations_details:upcoming_reservation_details'
    | 'reservations_details:past_reservation_details'
    | 'reservations_details:cancelled_reservation_details';
};

function LocationMap(props: Props) {
  const { className, item, isOpen, onClose, ...rest } = props;
  // TODO find out the right type
  const [map, setMap] = useState<any>(null);
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const isPC = deviceType === DESKTOP_SCREEN;
  const title = 'Location';
  const language = useSelector(getLanguage);
  const GOOGLE_MAP_URL = getGoogleMapURL(language);
  const PIN_ICON =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTYuMDAwMjQgMTIuOTk5OEM2LjAwMDI0IDcuNDc3NzYgMTAuNDc3MiAyLjk5OTc2IDE2LjAwMDIgMi45OTk3NkMyMS41MjIyIDIuOTk5NzYgMjYuMDAwMiA3LjQ3Nzc2IDI2LjAwMDIgMTIuOTk5OEMyNi4wMDAyIDE5Ljk5OTggMTYuMDAwMiAyOC45OTk4IDE2LjAwMDIgMjguOTk5OEMxNi4wMDAyIDI4Ljk5OTggNi4wMDAyNCAxOS45OTk4IDYuMDAwMjQgMTIuOTk5OFoiIGZpbGw9IiNENzQwMkUiLz48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTE5IDEzQzE5IDE0LjY1NjkgMTcuNjU2OSAxNiAxNiAxNkMxNC4zNDMxIDE2IDEzIDE0LjY1NjkgMTMgMTNDMTMgMTEuMzQzMSAxNC4zNDMxIDEwIDE2IDEwQzE3LjY1NjkgMTAgMTkgMTEuMzQzMSAxOSAxM1oiIGZpbGw9IndoaXRlIi8+PC9zdmc+';
  const MAP_CONTAINER = 'mapContainer';

  const market = useSelector(getMarket);
  const currency = useSelector(getCurrency);

  const isGlobalCurrency = useSelector(getIsGlobalCurrency);

  const scoreSummary = useSelector(getReviewScoreSummary);

  const dictionary = useSelector(getDictionary);
  const isDirectContract = item.contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

  const getLabelContent = useCallback(() => {
    const medium = item.media?.find((medium) => medium.url);
    const store = configureStore({ _i18n: { dictionary } });

    const content = isSP ? (
      <ReduxProvider store={store}>
        <MiniProviderSummary
          id={item.id}
          className={styles.providerSummary}
          name={item.name}
          reviewScore={item.reviewScore}
          totalReviews={item.totalReviews}
          reviewLabel={scoreSummary.averageRating?.name}
          media={medium}
          market={market.marketCode}
        />
      </ReduxProvider>
    ) : (
      <ReduxProvider store={store}>
        <ProviderSummary
          id={item.id}
          className={styles.providerSummary}
          name={item.name}
          reviewScore={item.reviewScore}
          totalReviews={item.totalReviews}
          reviewLabel={scoreSummary.averageRating?.name}
          starRating={item.starRating}
          media={medium}
          market={market.marketCode}
          language={language}
          currency={currency}
          isExpandedMap={true}
          isGlobalCurrency={isGlobalCurrency}
          isDirectContract={isDirectContract}
        />
      </ReduxProvider>
    );
    // Google API can take only DOM Node, not ReactNode
    const labelWrapper = document.createElement('div');
    ReactDOM.render(content, labelWrapper);

    return labelWrapper;
  }, [
    currency,
    isGlobalCurrency,
    isSP,
    item.id,
    item.media,
    item.name,
    item.reviewScore,
    item.starRating,
    item.totalReviews,
    language,
    market.marketCode,
    scoreSummary.averageRating,
    dictionary,
  ]);

  const handleLoaded = useCallback(() => {
    const center = { lat: item.location.lat, lng: item.location.lng };
    const zoom = 14;

    const options = {
      center: center,
      zoom: zoom,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      clickableIcons: false,
    };

    const mapDiv = document.getElementById(MAP_CONTAINER);
    const mapObj = new window.google.maps.Map(mapDiv, options);
    setMap(mapObj);
  }, [item.location.lat, item.location.lng]);

  useEffect(() => {
    // Don't load scriptjs as a dependency since we do not want this module be used on server side.
    const scriptjs = require('scriptjs');
    if (!window.google) {
      const googleMapURL = GOOGLE_MAP_URL;
      scriptjs(googleMapURL, handleLoaded);
    } else {
      handleLoaded();
    }
  }, [GOOGLE_MAP_URL, handleLoaded]);

  useEffect(() => {
    if (map) {
      const MarkerWithLabel = makeMarkerWithLabel(window.google.maps);
      const position = {
        lat: item.location.lat,
        lng: item.location.lng,
      };
      const labelContent = getLabelContent();
      new MarkerWithLabel({
        position: position,
        map: map,
        icon: PIN_ICON,
        labelContent: labelContent,
        labelClass: styles.label, // CSS class for the label
      });
    }
  }, [getLabelContent, item, map]);

  return (
    <div data-testid="locationMap-wrapper" {...rest}>
      <Dialog
        className={styles.noMaxLimit}
        contentClassName={styles.dialogContent}
        isOpen={isOpen}
        title={isPC ? null : title}
        onClose={onClose}
        isUseContentFlexBox={false}
        hasAnimation={true}
      >
        <>
          <div className={cx(className, styles.wrapper)} {...rest}>
            <div
              data-testid="locationMap-wrapper-mapContainer"
              className={styles.mapContainer}
              id={MAP_CONTAINER}
            />
          </div>
        </>
      </Dialog>
      <DataLayer
        pageName={`${props.pageName}:hotel_location_map_popup`}
        pageType="shop_item"
        siteSection="hotel"
      />
    </div>
  );
}

export default LocationMap;
